import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number; 
}

@Injectable({
  providedIn: 'root'
})
export class CacheService {
  private cache = new Map<string, CacheItem<any>>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; 

  /**
   * Armazena um item no cache
   */
  set<T>(key: string, data: T, ttl: number = this.DEFAULT_TTL): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  /**
   * Obtém um item do cache se ainda for válido
   */
  get<T>(key: string): T | null {
    const item = this.cache.get(key);
    
    if (!item) {
      return null;
    }

    const isExpired = Date.now() - item.timestamp > item.ttl;
    
    if (isExpired) {
      this.cache.delete(key);
      return null;
    }

    return item.data;
  }

  /**
   * Verifica se uma chave existe e não está expirada
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Remove um item específico do cache
   */
  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  /**
   * Limpa todo o cache
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Remove itens expirados do cache
   */
  cleanup(): void {
    const now = Date.now();
    for (const [key, item] of this.cache.entries()) {
      if (now - item.timestamp > item.ttl) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Retorna o tamanho atual do cache
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * Obtém dados do cache ou executa a função fornecida e armazena o resultado
   */
  getOrSet<T>(key: string, factory: () => Observable<T>, ttl?: number): Observable<T> {
    const cached = this.get<T>(key);
    
    if (cached !== null) {
      return of(cached);
    }

    return new Observable(observer => {
      factory().subscribe({
        next: (data) => {
          this.set(key, data, ttl);
          observer.next(data);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

  /**
   * Cache específico para dados de bancos (TTL mais longo)
   */
  getOrSetBanco(codigo: string, factory: () => Observable<any>): Observable<any> {
    const key = `banco_${codigo}`;
    const ttl = 30 * 60 * 1000; // 30 minutos para dados de bancos
    return this.getOrSet(key, factory, ttl);
  }

  /**
   * Cache específico para dados de contas bancárias
   */
  getOrSetContasBancarias(userId: string, factory: () => Observable<any[]>): Observable<any[]> {
    const key = `contas_bancarias_${userId}`;
    const ttl = 10 * 60 * 1000; // 10 minutos para contas bancárias
    return this.getOrSet(key, factory, ttl);
  }

  /**
   * Cache específico para dados de contas contábeis
   */
  getOrSetContasContabeis(userId: string, factory: () => Observable<any[]>): Observable<any[]> {
    const key = `contas_contabeis_${userId}`;
    const ttl = 15 * 60 * 1000; // 15 minutos para contas contábeis
    return this.getOrSet(key, factory, ttl);
  }

  /**
   * Invalida cache específico por padrão
   */
  invalidatePattern(pattern: string): void {
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    }
  }
} 
